import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { 
  fetchLeaves
} from "../../redux/applyLeaveSlice";
import { 
  fetchAssignedLeavesByEmail,
  fetchAssignedLeaves
} from "../../redux/assignLeaveSlice";
import { 
  fetchAttendanceByEmail 
} from "../../redux/attendanceSlice";
import {
  fetchEmployeeSalaryByTeacherEmail,
  fetchActiveEmployeeSalary,
} from "../../redux/employeeSalarySlice";

const EmployeeDetailsCard = ({ employee }) => {
  const dispatch = useDispatch();
  
  // State to manage active tab
  const [activeTab, setActiveTab] = useState("Employment");

  // Tab data for dynamic rendering
  const tabs = ["Employment", "Leaves", "Attendance", "Financial"];

  // Redux selectors
  const { leaves } = useSelector((state) => state.leaves);
  const { assignedLeaves } = useSelector((state) => state.assignLeave);
  const { attendanceRecords } = useSelector((state) => state.attendance);
  const { employeeSalaries, activeSalary } = useSelector((state) => state.employeeSalaries);

  // Calculate years of service
  const calculateYearsOfService = () => {
    if (!employee.createdAt) return 0;
    const joiningDate = new Date(employee.createdAt);
    const currentDate = new Date();
    const diffTime = Math.abs(currentDate - joiningDate);
    const diffYears = Math.ceil(diffTime / (1000 * 60 * 60 * 24 * 365));
    return diffYears;
  };

  // Calculate profile completion percentage
  const calculateProfileCompletion = () => {
    const profileFields = [
      employee.firstName, employee.lastName, employee.email, employee.phone,
      employee.location, employee.cnic, employee.address, employee.dob,
      employee.gender, employee.religion, employee.department, employee.designation
    ];
    const completedFields = profileFields.filter(field => field && field !== '').length;
    return Math.round((completedFields / profileFields.length) * 100);
  };

  // Calculate leave statistics
  const calculateLeaveStats = () => {
    console.log("All leaves from Redux:", leaves);
    console.log("All assigned leaves from Redux:", assignedLeaves);
    console.log("Current employee email:", employee.email);
    
    const employeeLeaves = leaves.filter(leave => 
      leave.teacherEmail === employee.email || leave.adminEmail === employee.email
    );
    
    console.log("Filtered employee leaves:", employeeLeaves);
    
    const approvedLeaves = employeeLeaves.filter(leave => leave.status === 'Approved');
    const pendingLeaves = employeeLeaves.filter(leave => leave.status === 'Pending');
    const rejectedLeaves = employeeLeaves.filter(leave => leave.status === 'Rejected');

    // Get assigned leave balance
    const employeeAssignedLeaves = assignedLeaves.filter(leave => 
      leave.teacherEmail === employee.email
    );
    
    console.log("Filtered employee assigned leaves:", employeeAssignedLeaves);

    return {
      totalApplied: employeeLeaves.length,
      approved: approvedLeaves.length,
      pending: pendingLeaves.length,
      rejected: rejectedLeaves.length,
      assignedLeaves: employeeAssignedLeaves
    };
  };

  // Calculate attendance statistics
  const calculateAttendanceStats = () => {
    const employeeAttendance = attendanceRecords.filter(record => 
      record.teacherEmail === employee.email
    );

    const currentMonth = new Date().getMonth();
    const currentYear = new Date().getFullYear();
    
    const currentMonthAttendance = employeeAttendance.filter(record => {
      const recordDate = new Date(record.date);
      return recordDate.getMonth() === currentMonth && recordDate.getFullYear() === currentYear;
    });

    const present = currentMonthAttendance.filter(record => record.status === 'Present').length;
    const absent = currentMonthAttendance.filter(record => record.status === 'Absent').length;
    const late = currentMonthAttendance.filter(record => record.status === 'Late').length;
    const onLeave = currentMonthAttendance.filter(record => record.status === 'On Leave').length;

    return {
      present,
      absent,
      late,
      onLeave,
      totalDays: currentMonthAttendance.length
    };
  };

  // Calculate financial statistics
  const calculateFinancialStats = () => {
    // Find salary specifically for this employee
    const employeeSalary = employeeSalaries.find(salary => 
      salary.teacherEmail === employee.email && salary.isActive
    ) || activeSalary;

    // Only return salary data if this employee has a salary record
    if (!employeeSalary || employeeSalary.teacherEmail !== employee.email) {
      return {
        hasSalary: false,
        baseSalary: 0,
        allowanceAmount: 0,
        bonusAmount: 0,
        overtimeRate: 0
      };
    }

    return {
      hasSalary: true,
      baseSalary: employeeSalary.baseSalary || 0,
      allowanceAmount: employeeSalary.allowanceAmount || 0,
      bonusAmount: employeeSalary.bonusAmount || 0,
      overtimeRate: employeeSalary.overtimeRate || 0
    };
  };

  // Fetch employee data when component mounts
  useEffect(() => {
    if (employee?.email) {
      console.log("Fetching data for employee:", employee.email);
      
      // Try to fetch leaves for specific employee
      dispatch(fetchLeaves({ teacherEmail: employee.email }))
        .then((result) => {
          console.log("Leaves fetch result:", result);
          if (result.meta.requestStatus === 'rejected') {
            console.log("Falling back to fetch all leaves");
            // Fallback: fetch all leaves and filter on frontend
            dispatch(fetchLeaves());
          }
        })
        .catch((error) => {
          console.error("Error fetching leaves:", error);
          // Fallback: fetch all leaves
          dispatch(fetchLeaves());
        });
        
      // Try to fetch assigned leaves for specific employee
      dispatch(fetchAssignedLeavesByEmail(employee.email))
        .then((result) => {
          console.log("Assigned leaves fetch result:", result);
          if (result.meta.requestStatus === 'rejected') {
            console.log("Falling back to fetch all assigned leaves");
            // Fallback: fetch all assigned leaves and filter on frontend
            dispatch(fetchAssignedLeaves());
          }
        })
        .catch((error) => {
          console.error("Error fetching assigned leaves:", error);
          // Fallback: fetch all assigned leaves
          dispatch(fetchAssignedLeaves());
        });
        
      dispatch(fetchAttendanceByEmail(employee.email));
      dispatch(fetchEmployeeSalaryByTeacherEmail(employee.email));
      dispatch(fetchActiveEmployeeSalary(employee.email));
    }
  }, [dispatch, employee?.email]);

  const yearsOfService = calculateYearsOfService();
  const profileCompletion = calculateProfileCompletion();
  const leaveStats = calculateLeaveStats();
  const attendanceStats = calculateAttendanceStats();
  const financialStats = calculateFinancialStats();

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      {/* Personal Details Section */}
      <div className="bg-white col-span-1 rounded-lg shadow p-4">
        <div className="flex items-center space-x-4">
          <img
            src={employee.profilePicture || "https://via.placeholder.com/100"}
            alt="User avatar"
            className="w-20 h-20 rounded-full object-cover"
          />
          <div>
            <h2 className="text-xl font-semibold">{`${employee.firstName} ${employee.lastName}`}</h2>
            <p className="text-sm text-gray-500">{yearsOfService} Year{yearsOfService !== 1 ? 's' : ''} Completed</p>
            <p className="text-sm text-blue-500">{profileCompletion}% Profile Completed</p>
          </div>
        </div>
        <hr className="my-4" />
        <h3 className="font-bold text-lg">Personal Details</h3>
        <ul className="text-sm text-gray-600 space-y-2 mt-2">
          <li>Father Name: {employee.fatherName || "N/A"}</li>
          <li>Email: {employee.email}</li>
          <li>Date Of Birth: {employee.dob ? new Date(employee.dob).toLocaleDateString() : "N/A"}</li>
          <li>Gender: {employee.gender || "N/A"}</li>
          <li>Religion: {employee.religion || "N/A"}</li>
          <li>Contact: {employee.phone || "N/A"}</li>
          <li>City: {employee.location || "N/A"}</li>
          <li>Country: Pakistan</li>
        </ul>
       
        <div className="mt-4 bg-gray-100 p-4 rounded">
          <p className="text-blue-500 font-bold">Salary</p>
          {financialStats.hasSalary ? (
            <p className="text-xl font-semibold">Rs. {financialStats.baseSalary?.toLocaleString() || 0}/month</p>
          ) : (
            <p className="text-sm text-gray-500">No salary record found</p>
          )}
        </div>
        <p className="mt-2 text-gray-700">Job Description</p>
      </div>

      {/* Details Section with Tabs */}
      <div className="bg-white col-span-2 h-auto rounded-lg shadow p-4">
        <h3 className="font-bold text-lg mb-4">Details</h3>

        {/* Tabs */}
        <div className="flex border-b mb-4">
          {tabs.map((tab) => (
            <button
              key={tab}
              className={`px-4 py-2 text-sm font-medium focus:outline-none ${
                activeTab === tab
                  ? "text-[#241763] border-b-2 border-[#241763]"
                  : "text-gray-500 hover:text-[#241763]"
              }`}
              onClick={() => setActiveTab(tab)}
            >
              {tab}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <div className="p-2">
          {activeTab === "Employment" && (
            <div>
              <h3 className="font-bold text-lg">Employment Details</h3>
              <div className="grid grid-cols-2 text-sm text-gray-600 gap-4 mt-2">
                <div>
                  <li>Employee Code: {employee.lmsId || "N/A"}</li>
                  <li>Department: {employee.department || "Not Assigned"}</li>
                  <li>Reporting To: Not Assigned</li>
                  <li>Joining Date: {employee.createdAt ? new Date(employee.createdAt).toLocaleDateString() : "N/A"}</li>
                  <li>Probation End: -</li>
                </div>
                <div>
                  <li>Blood Group: N/A</li>
                  <li>CNIC: {employee.cnic || "N/A"}</li>
                  <li>CNIC Expiry: N/A</li>
                  <li>Phone Number: {employee.phone || "N/A"}</li>
                  <li>Complete Address: {employee.address || "N/A"}</li>
                </div>
              </div>
            </div>
          )}

          {activeTab === "Leaves" && (
            <div>
              <h3 className="font-bold text-lg">Leave Details</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <h4 className="font-semibold text-sm text-gray-700 mb-2">Leave Statistics</h4>
                  <ul className="text-sm text-gray-600 space-y-1">
                    <li>Total Applied: {leaveStats.totalApplied}</li>
                    <li>Approved: {leaveStats.approved}</li>
                    <li>Pending: {leaveStats.pending}</li>
                    <li>Rejected: {leaveStats.rejected}</li>
                  </ul>
                  {leaveStats.totalApplied === 0 && (
                    <p className="text-xs text-gray-400 mt-2">No leave applications found</p>
                  )}
                </div>
                <div>
                  <h4 className="font-semibold text-sm text-gray-700 mb-2">Assigned Leave Balance</h4>
                  {leaveStats.assignedLeaves.length > 0 ? (
                    <ul className="text-sm text-gray-600 space-y-1">
                      {leaveStats.assignedLeaves.map((leave, index) => (
                        <li key={index}>
                          {leave.leaveType}: {leave.numberOfLeave} days
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <div>
                      <p className="text-sm text-gray-500">No leave balance assigned</p>
                      <p className="text-xs text-gray-400 mt-1">
                        Contact HR to assign leave balance
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {activeTab === "Attendance" && (
            <div>
              <h3 className="font-bold text-lg">Attendance Details</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <h4 className="font-semibold text-sm text-gray-700 mb-2">Current Month ({attendanceStats.totalDays} days)</h4>
                  <ul className="text-sm text-gray-600 space-y-1">
                    <li>Present Days: {attendanceStats.present}</li>
                    <li>Absent Days: {attendanceStats.absent}</li>
                    <li>Late Arrivals: {attendanceStats.late}</li>
                    <li>On Leave: {attendanceStats.onLeave}</li>
                  </ul>
                </div>
                <div>
                  <h4 className="font-semibold text-sm text-gray-700 mb-2">Attendance Rate</h4>
                  <div className="text-2xl font-bold text-green-600">
                    {attendanceStats.totalDays > 0 
                      ? Math.round((attendanceStats.present / attendanceStats.totalDays) * 100) 
                      : 0}%
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === "Financial" && (
            <div>
              <h3 className="font-bold text-lg">Financial Details</h3>
              {financialStats.hasSalary ? (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <h4 className="font-semibold text-sm text-gray-700 mb-2">Salary Structure</h4>
                    <ul className="text-sm text-gray-600 space-y-1">
                      <li>Base Salary: Rs. {financialStats.baseSalary?.toLocaleString() || 0}</li>
                      <li>Allowance: Rs. {financialStats.allowanceAmount?.toLocaleString() || 0}</li>
                      <li>Bonus: Rs. {financialStats.bonusAmount?.toLocaleString() || 0}</li>
                      <li>Overtime Rate: Rs. {financialStats.overtimeRate || 0}/hour</li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-semibold text-sm text-gray-700 mb-2">Total Monthly</h4>
                    <div className="text-2xl font-bold text-blue-600">
                      Rs. {(financialStats.baseSalary + financialStats.allowanceAmount + financialStats.bonusAmount)?.toLocaleString() || 0}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-center py-8">
                  <div className="text-gray-500 text-lg mb-2">No Salary Record Found</div>
                  <p className="text-sm text-gray-400">
                    This employee doesn't have a salary record in the system.
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default EmployeeDetailsCard;
